import type { StructuredMultiSearchRequest } from '@massilia/queries'

import { format, parse } from 'date-fns'
import { fr } from 'date-fns/locale'

import type { FlightSearchSchemaType } from '@/lib/schemas'

/**
 * Résumé d'une ligne affiché sous « Modifier la recherche » dans la barre mobile :
 * le formulaire n'étant plus déplié, c'est le seul rappel de ce qui est recherché.
 * Format : « Marseille → Alger · 12 sept. · 2 voyageurs ».
 */
export function buildFlightSearchSummary(data: FlightSearchSchemaType): string {
  const firstLeg = data.flight[0]
  const lastLeg = data.flight[data.flight.length - 1]
  if (!firstLeg || !lastLeg) return ''

  const dates = [firstLeg.date.from, firstLeg.date.to]
    .filter((date): date is Date => date instanceof Date)
    .map((date) => format(date, 'd MMM', { locale: fr }))

  return joinSummary([
    `${firstLeg.departure.label} → ${lastLeg.destination.label}`,
    dates.join(' - '),
    formatPassengers(countPassengers(data.passengers)),
  ])
}

export function buildMultiSearchSummary(params: StructuredMultiSearchRequest): string {
  const firstLeg = params.flights[0]
  const lastLeg = params.flights[params.flights.length - 1]
  if (!firstLeg || !lastLeg) return ''

  const range = params.dateRanges[0]
  const dates = range
    ? [range.from, range.to]
        .filter((iso) => iso.length > 0)
        .map((iso) => format(parse(iso, 'yyyy-MM-dd', new Date()), 'd MMM', { locale: fr }))
    : []

  return joinSummary([
    `${firstLeg.departure.label ?? firstLeg.departure.iataCode} → ${lastLeg.destination.label ?? lastLeg.destination.iataCode}`,
    // Les recherches multi portent sur des périodes, pas des dates fixes.
    dates.length > 1 ? `du ${dates[0]} au ${dates[1]}` : dates[0],
    formatPassengers(countPassengers(params.passengers)),
  ])
}

function countPassengers(passengers: Record<string, number>): number {
  return Object.values(passengers).reduce((total, count) => total + count, 0)
}

function formatPassengers(count: number): string {
  return `${count} voyageur${count > 1 ? 's' : ''}`
}

function joinSummary(parts: (string | undefined)[]): string {
  return parts.filter((part) => part !== undefined && part.length > 0).join(' · ')
}
